import { FieldData } from '@/components/tables/field-carbon-table.component';
import { TableBody, TableBorderless, TableCell, TableHead, TableRow } from '@/components/ui/table';
import { FarmData } from '@/modules/Report/Report.schema';
import { cn } from '@/styles/styles.utils';

export type FarmCarbonTableProps = {
  className?: string;
  data: FarmData;
};

export const FarmCarbonTable = ({ data, className }: FarmCarbonTableProps) => {
  const fieldTypes = data.carbonInfo.byFieldType.map((ft) => ft.fieldType);

  return (
    <TableBorderless className={className}>
      <TableBody className="mt-6 border-t-0">
        <TableRow>
          <TableCell className="w-1/4"></TableCell>
          {fieldTypes.map((ft) => (
            <TableCell key={ft} className="text-right">
              {ft}
            </TableCell>
          ))}
          <TableCell className="text-right font-bold">Total</TableCell>
        </TableRow>
      </TableBody>

      {/*Data rows */}
      <TableBody className="border-l-8 border-secondary">
        {/* Number of Fields */}
        <TableRow>
          <TableHead>Number of Fields</TableHead>
          {data.farmInfo.fieldCountsByType.map((fc) => (
            <TableCell key={fc.fieldType} className="text-right">
              {fc.count}
            </TableCell>
          ))}
          <TableCell className="text-right font-bold">{data.farmInfo.totalFields}</TableCell>
        </TableRow>

        {/* Total Carbon Stock (t) */}
        <TableRow>
          <TableHead>Total Carbon Stock (t)</TableHead>
          {data.carbonInfo.byFieldType.map((ft) => (
            <TableCell key={ft.fieldType} className="text-right">
              {ft.totalCarbonStock}
            </TableCell>
          ))}
          <TableCell className="text-right font-bold">{data.carbonInfo.totalCarbonStock}</TableCell>
        </TableRow>

        {/* Average Carbon Stock (t/ha) */}
        <TableRow>
          <TableHead>Average Carbon Stock (t/ha)</TableHead>
          {data.carbonInfo.byFieldType.map((ft) => (
            <TableCell key={ft.fieldType} className="text-right">
              {ft.averageCarbonPerHectare}
            </TableCell>
          ))}
          <TableCell className="text-right font-bold">{data.carbonInfo.averageCarbonPerHectare}</TableCell>
        </TableRow>

        {/* Standard Deviation of Carbon Stock (t/ha) */}
        <TableRow>
          <TableHead>Standard Deviation of Carbon Stock (t/ha)</TableHead>
          {data.carbonInfo.byFieldType.map((ft) => (
            <TableCell key={ft.fieldType} className="text-right">
              {ft.carbonStdDeviation}
            </TableCell>
          ))}
          <TableCell className="text-right font-bold">{data.carbonInfo.carbonStdDeviation}</TableCell>
        </TableRow>
      </TableBody>
    </TableBorderless>
  );
};
export type FieldCarbonTableProps = {
  className?: string;
  isField?: boolean;
  data: FieldData;
};

//the isField prop is used to determine if the comparison to farm row should be displayed. This is used when this table is used on field level reports.
export const FieldCarbonTable = ({ data, isField, className }: FieldCarbonTableProps) => {
  return (
    <TableBorderless className={cn('border-l-8 border-secondary', className)}>
      <TableBody className="border-left-4 border-t-0">
        <TableRow>
          <TableHead className="w-1/2">Total Carbon Stock (t)</TableHead>
          <TableCell className="text-right">{data.carbonInfo.totalCarbonStock}</TableCell>
        </TableRow>
        <TableRow>
          <TableHead className="w-1/2">Average Carbon Stock (t/ha)</TableHead>
          <TableCell className="text-right">{data.carbonInfo.averageCarbonPerHectare}</TableCell>
        </TableRow>
        <TableRow>
          <TableHead className="w-1/2">Standard Deviation of Carbon Stock (t/ha)</TableHead>
          <TableCell className="text-right">{data.carbonInfo.carbonStdDeviation}</TableCell>
        </TableRow>
        {isField && data.carbonInfo.comparisonToFarm && (
          <TableRow>
            <TableHead className="w-1/2">Comparison to Farm (% of farm average t/ha)</TableHead>
            <TableCell className="text-right">{data.carbonInfo.comparisonToFarm}</TableCell>
          </TableRow>
        )}
      </TableBody>
    </TableBorderless>
  );
};
